import { Model, modelRegistry } from './Model';
import { Order, CashRegister, UserTransaction } from '../models';
import { I18n } from '../wrappers/I18n';
import ApiService from '../services/ApiService';
import FiscalServiceLegacy from '../fiscal/FiscalServiceLegacy';
import HashGen from '../utilities/HashGen';
import Store from '../wrappers/Store';
import { dateFormatter } from '../utilities/dateFormatter';
import moment from 'moment-timezone';
export class Shift extends Model {
    static entity = 'shifts';
    static primaryKey = 'hash';
    static syncable = true;
    /** The report dialogs' start/end format (ReportFilter in the POS). */
    static REPORT_PERIOD_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
    id = null;
    hash = '';
    cash_register_id = null;
    name = null;
    opened_at = null;
    closed_at = null;
    created_at = null;
    updated_at = null;
    cashRegister;
    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(HashGen.regular()),
            cash_register_id: this.attr(null),
            name: this.attr(null),
            opened_at: this.attr(null),
            closed_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            cashRegister: this.attr(undefined, value => value ? new CashRegister(value) : undefined),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    /**
     * Get the currently opened Shift
     */
    static current() {
        const curCashRegister = CashRegister.current();
        if (!curCashRegister)
            return undefined;
        return Shift.all().find(shift => !shift.closed_at && shift.cash_register_id === curCashRegister.id);
    }
    getOpenedAt() {
        return moment.utc(this.opened_at).local();
    }
    getClosedAt() {
        return this.closed_at ? moment.utc(this.closed_at).local() : null;
    }
    /**
     * The shift report's period for this shift (docs/ShiftReportPickerTechSpec.md): opened →
     * closed, or opened → now while it is open, in the report dialogs' local datetime format.
     */
    getReportPeriod(now = moment()) {
        const closedAt = Shift.inVenueTime(this.closed_at);
        return {
            start: Shift.inVenueTime(this.opened_at).format(Shift.REPORT_PERIOD_FORMAT),
            end: (closedAt ?? now.clone().tz(dateFormatter().getTimezone())).format(Shift.REPORT_PERIOD_FORMAT),
        };
    }
    /** `<name> · <start> – <end>`, to the minute; the open shift ends in `openText`, an unnamed one has no name part. */
    getPickerLabel(openText) {
        const { formatDateTimeShort } = dateFormatter();
        const closedAt = Shift.inVenueTime(this.closed_at);
        const period = `${formatDateTimeShort(Shift.inVenueTime(this.opened_at))} – ${closedAt ? formatDateTimeShort(closedAt) : openText}`;
        return this.name ? `${this.name} · ${period}` : period;
    }
    /**
     * A stored UTC time in the venue's time zone. Report periods are read by the server in the
     * venue's zone (aria-api ReportsController), so the picker's times must be venue wall-clock
     * times whatever zone the device or the admin's browser is in.
     */
    static inVenueTime(value) {
        return value ? moment.utc(value).tz(dateFormatter().getTimezone()) : null;
    }
    /**
     * The shifts of one cash register the shift report can be produced for — the server's last
     * 30 days, open shift first. Returned as instances and NOT stored: the store holds only the
     * open shift, and Shift.current() must keep meaning that. Throws when the request fails.
     */
    static async fetchForCashRegister(cashRegisterId, options = {}) {
        const response = await ApiService.get(options.admin ? '/admin/shifts' : '/shifts', {
            params: { cash_register_id: cashRegisterId }
        });
        return (response.data.shifts ?? []).map(shift => new Shift(shift));
    }
    /**
     * The picker list with the device's own open shift on top when the server list lacks it (a
     * shift opened offline and not synced yet). Matched by hash, never duplicated.
     */
    static withCurrent(list, current) {
        if (!current || list.some(shift => shift.hash === current.hash))
            return list;
        return [current, ...list];
    }
    getOrders() {
        return Order.all().filter(order => {
            return (this.id && this.id === order.shift_id) || (this.hash && this.hash === order.shift_hash);
        });
    }
    getUserTransactions() {
        return UserTransaction.all().filter(transaction => {
            return (this.id && this.id === transaction.shift_id) || (this.hash && this.hash === transaction.shift_hash);
        });
    }
    /**
     * Open Shift. The name (venue setting shift_name_required) is sent only when
     * given, so an unnamed open posts the same empty body as before.
     */
    static async open(name) {
        try {
            const curCashRegister = CashRegister.current();
            if (!curCashRegister) {
                return {
                    success: false,
                    error: I18n.t('cash_register.registration_needed_short')
                };
            }
            if (!Store.get('isOnline')) {
                const shift = new Shift({
                    opened_at: moment().utc().format(Shift.DATE_FORMAT),
                    cash_register_id: curCashRegister.id,
                    name: name || null,
                });
                shift.markForSync('create');
                shift.$save();
                return { success: true };
            }
            // Open shift on server
            const response = await ApiService.post('/shift/open', name ? { name } : {});
            Shift.create({ data: [response.data.shift] });
            Shift.syncTerminals({ items: [{ action: 'post', record: response.data.shift }] });
            return { success: true };
        }
        catch (error) {
            return {
                success: false,
                error: I18n.t('general.action_failed')
            };
        }
    }
    /**
     * Close Shift
     */
    static async close() {
        const curCashRegister = CashRegister.current();
        try {
            if (!curCashRegister) {
                return {
                    success: false,
                    error: I18n.t('cash_register.registration_needed_short')
                };
            }
            if (I18n.c('order.registerMethod') === 'legacy') {
                const result = await FiscalServiceLegacy.printDailyReport();
                if (!result.success) {
                    return {
                        success: false,
                        error: result.error?.message || I18n.t('general.action_failed')
                    };
                }
            }
            let shift;
            if (!Store.get('isOnline')) {
                shift = Shift.current();
                if (!shift) {
                    return {
                        success: false,
                        error: I18n.t('general.action_failed')
                    };
                }
                shift.closed_at = moment().utc().format(Shift.DATE_FORMAT);
                shift.markForSync('update');
                shift.$save();
            }
            else {
                const response = await ApiService.post('/shift/close', {});
                Shift.insert({ data: [response.data.shift] });
                shift = new Shift(response.data.shift);
            }
            Shift.syncTerminals({ items: [{ action: 'patch', record: shift.$toJson() }] });
            return { success: true };
        }
        catch (error) {
            return {
                success: false,
                error: I18n.t('general.action_failed')
            };
        }
    }
}
export default Shift;
modelRegistry.register('Shift', Shift);
//# sourceMappingURL=Shift.js.map